/* eslint-disable jsx-a11y/alt-text */
import { Grid, Box, Typography, Divider } from '@material-ui/core';
import React from 'react';
import { simpleDateFormatter2, moneyFormatEmpty } from '../Shared/colorFormat';

const printcolor = '#222';
const dottedLine = '........................................';

const formTitles: any = {
  entitlement: {
    ar: 'مخالصة تسليم فلاشة',
    en: 'Media Delivery Clearance',
  },
  financialCompensation: {
    ar: 'مخالصة تعويض مالية',
    en: 'Financial Compensation Clearance',
  },
  clearanceWithDeposit: {
    ar: 'مخالصة الغاء العقد مع استرجاع العربون',
    en: 'Contract Cancellation With Deposit Return',
  },
  clearanceNoDeposit: {
    ar: 'مخالصة الغاء العقد بدون استرجاع المستحقات المالية',
    en: 'Contract Cancellation Without Financial Return',
  },
};

const safeText = (value: any) => {
  if (value === 0) return '0';
  if (!value) return dottedLine;
  const text = String(value).trim();
  return text || dottedLine;
};

const firstNonEmpty = (...values: any[]) => {
  for (const value of values) {
    if (value === 0) return value;
    if (value === undefined || value === null) continue;
    if (`${value}`.trim() !== '') {
      return value;
    }
  }
  return '';
};

const toSafeDateValue = (value: any) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const dt = new Date(year, month - 1, day, 12, 0, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const safeDate = (value: any) => {
  if (!value) return dottedLine;
  const dateValue = toSafeDateValue(value);
  if (!dateValue) return dottedLine;
  return simpleDateFormatter2(dateValue);
};

const parseNoteMeta = (note: any) => {
  if (!note) {
    return null;
  }

  if (typeof note !== 'string') {
    if (typeof note === 'object') {
      return note;
    }
    return null;
  }

  try {
    const parsed = JSON.parse(note);
    if (parsed && typeof parsed === 'object') {
      const extra =
        parsed?.extra && typeof parsed.extra === 'object' ? parsed.extra : {};
      return {
        ...parsed,
        ...extra,
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

export class CustomerClearancePrint extends React.PureComponent<any, any> {
  renderHeader = (company: any) => (
    <Grid item xs={12}>
      <img
        src={company?.header}
        alt={company?.name}
        height="auto"
        width="100%"
        style={{
          objectFit: 'contain',
        }}
      />
    </Grid>
  );

  renderFooter = (company: any) => (
    <Grid item xs={12}>
      <img
        src={company?.footer}
        alt={company?.name}
        height={50}
        width="100%"
        style={{
          objectFit: 'contain',
          alignSelf: 'flex-start',
        }}
      />
    </Grid>
  );

  renderTitle = (title: string) => {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 18,
          marginTop: 12,
        }}
      >
        <Typography
          style={{
            fontWeight: 'bold',
            fontSize: 24,
            color: printcolor,
            textDecoration: 'underline',
            textUnderlineOffset: 6,
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  };

  renderDocInfo = (docNo: any, docDate: any, isRTL: boolean) => {
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Typography style={{ fontSize: 12, color: printcolor }}>
          {isRTL ? 'رقم المستند:' : 'Document No:'} {safeText(docNo)}
        </Typography>
        <Typography style={{ fontSize: 12, color: printcolor }}>
          {isRTL ? 'تاريخ الإقرار:' : 'Clearance Date:'} {safeDate(docDate)}
        </Typography>
      </Box>
    );
  };

  renderMainArabicStatement = ({
    formType,
    docDate,
    customerName,
    civilId,
    companyName,
    eventDate,
    agreementNo,
    amountText,
    iban,
    reason,
    deliveryDetails,
  }: any) => {
    if (formType === 'entitlement') {
      return `إنه في يوم ${safeDate(docDate)}، أقر أنا / ${safeText(customerName)} حامل البطاقة الشخصية رقم ${safeText(civilId)}، بأنني قد استلمت من شركة ${safeText(companyName)} كافة المواد المصورة الخاصة بي (فيديو + صور) المتعلقة بالحفل بتاريخ ${safeDate(eventDate)}.

تفاصيل المواد المستلمة: ${safeText(deliveryDetails)}.

وبناءً عليه، أقر بإبراء ذمة شركة ${safeText(companyName)} إبراءً تاماً ونهائياً وشاملاً، ولا يحق لي الرجوع عليها بأي مطالبة مالية أو قانونية حالية أو مستقبلية تخص موضوع هذا الإقرار.`;
    }

    if (formType === 'financialCompensation') {
      return `إنه في يوم ${safeDate(docDate)}، أقر أنا / ${safeText(customerName)} حامل البطاقة الشخصية رقم ${safeText(civilId)}، بأنني استلمت من شركة ${safeText(companyName)} تعويضاً مالياً نهائياً وقدره ${safeText(amountText)} ريال قطري فقط لا غير.

    وذلك عن الأضرار/الملابسات المرتبطة بالحفل بتاريخ ${safeDate(eventDate)} بموجب عقد الاتفاق رقم ${safeText(agreementNo)}.

وتم سداد مبلغ التعويض عبر الآيبان البنكي التالي: ${safeText(iban)}.

وصف سبب التعويض: ${safeText(reason)}.

وبناءً عليه، أقر بإبراء ذمة شركة ${safeText(companyName)} إبراءً تاماً ونهائياً وشاملاً، ولا يحق لي الرجوع عليها بأي مطالبة مالية أو قانونية حالية أو مستقبلية تخص موضوع هذا الإقرار.`;
    }

    if (formType === 'clearanceWithDeposit') {
      return `إنه في يوم ${safeDate(docDate)}، أقر أنا / ${safeText(customerName)} حامل البطاقة الشخصية رقم ${safeText(civilId)}، بأنه تم إلغاء عقد الاتفاق رقم ${safeText(agreementNo)} الخاص بالحفل بتاريخ ${safeDate(eventDate)}.

وجاء الإلغاء للسبب التالي: ${safeText(reason)}.

كما أقر بأنني استلمت مبلغ العربون المدفوع وقدره ${safeText(amountText)} ريال قطري فقط لا غير، وذلك عبر الآيبان البنكي التالي: ${safeText(iban)}.

وبناءً عليه، أقر بإبراء ذمة شركة ${safeText(companyName)} إبراءً تاماً ونهائياً وشاملاً، ولا يحق لي الرجوع عليها بأي مطالبة مالية أو قانونية حالية أو مستقبلية تخص موضوع هذا الإقرار.`;
    }

    return `إنه في يوم ${safeDate(docDate)}، أقر أنا / ${safeText(customerName)} حامل البطاقة الشخصية رقم ${safeText(civilId)}، بأنه تم إلغاء عقد الاتفاق رقم ${safeText(agreementNo)}.

وجاء الإلغاء للسبب التالي: ${safeText(reason)}.

كما أقر وأوافق صراحةً على أنه لا يحق لي المطالبة بأي مستحقات مالية تجاه شركة ${safeText(companyName)} نتيجة إلغاء هذا العقد.

وبناءً عليه، أقر بإبراء ذمة شركة ${safeText(companyName)} إبراءً تاماً ونهائياً وشاملاً، ولا يحق لي الرجوع عليها بأي مطالبة مالية أو قانونية حالية أو مستقبلية تخص موضوع هذا الإقرار.`;
  };

  renderMainEnglishStatement = ({
    formType,
    customerName,
    civilId,
    companyName,
    eventDate,
    agreementNo,
    amountText,
    iban,
    reason,
    deliveryDetails,
  }: any) => {
    if (formType === 'entitlement') {
      return `I, ${safeText(customerName)} holder of ID No. ${safeText(civilId)}, acknowledge receiving all media files (video + photos) from ${safeText(companyName)} for the event dated ${safeDate(eventDate)}.

Delivered media details: ${safeText(deliveryDetails)}.

Accordingly, I fully and finally release ${safeText(companyName)} from any present or future financial or legal claim related to this statement.`;
    }

    if (formType === 'financialCompensation') {
      return `I, ${safeText(customerName)} holder of ID No. ${safeText(civilId)}, acknowledge receiving a final financial compensation of ${safeText(amountText)} QAR from ${safeText(companyName)} related to the event dated ${safeDate(eventDate)} under agreement No. ${safeText(agreementNo)}.

Payment IBAN: ${safeText(iban)}.
Reason: ${safeText(reason)}.

Accordingly, I fully and finally release ${safeText(companyName)} from any present or future financial or legal claim related to this statement.`;
    }

    if (formType === 'clearanceWithDeposit') {
      return `I, ${safeText(customerName)} holder of ID No. ${safeText(civilId)}, acknowledge cancellation of agreement No. ${safeText(agreementNo)} for the event dated ${safeDate(eventDate)} due to: ${safeText(reason)}.

I also acknowledge receiving the paid deposit amounting to ${safeText(amountText)} QAR via IBAN: ${safeText(iban)}.

Accordingly, I fully and finally release ${safeText(companyName)} from any present or future financial or legal claim related to this statement.`;
    }

    return `I, ${safeText(customerName)} holder of ID No. ${safeText(civilId)}, acknowledge cancellation of agreement No. ${safeText(agreementNo)} due to: ${safeText(reason)}.

I further acknowledge that I am not entitled to claim any financial dues from ${safeText(companyName)}.

Accordingly, I fully and finally release ${safeText(companyName)} from any present or future financial or legal claim related to this statement.`;
  };

  renderSignatureBlock = ({
    isRTL,
    customerName,
    docDate,
    customerPhone,
  }: any) => {
    return (
      <Box
        border={1}
        borderColor="#ddd"
        p={2}
        mt={3}
        style={{ borderRadius: 6, backgroundColor: '#fafafa' }}
      >
        <Typography
          style={{
            fontSize: 12,
            color: printcolor,
            fontWeight: 'bold',
            marginBottom: 10,
          }}
        >
          {isRTL ? 'المقر بما فيه:' : 'Declared and Signed By:'}
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography style={{ fontSize: 11, color: printcolor }}>
              {isRTL ? 'الاسم:' : 'Name:'} {safeText(customerName)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={{ fontSize: 11, color: printcolor }}>
              {isRTL ? 'رقم الهاتف:' : 'Phone:'} {safeText(customerPhone)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={{ fontSize: 11, color: printcolor }}>
              {isRTL ? 'التوقيع:' : 'Signature:'} {dottedLine}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={{ fontSize: 11, color: printcolor }}>
              {isRTL ? 'التاريخ:' : 'Date:'} {safeDate(docDate)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  render() {
    const data = this.props?.printData || {};
    const company = this.props?.company || {};
    const isRTL = !!data?.isRTL;
    const formType = data?.formType || 'entitlement';

    const customer = data?.customer || data?.custvalue || {};
    const metadata =
      data?.formMeta ||
      parseNoteMeta(data?.note) ||
      parseNoteMeta(data?.info) ||
      {};

    const companyName = isRTL
      ? company?.nameAr || company?.name
      : company?.name || company?.nameAr;

    const customerName = isRTL
      ? customer?.nameAr ||
        customer?.name ||
        data?.customerNameAr ||
        data?.customerName
      : customer?.name ||
        customer?.nameAr ||
        data?.customerName ||
        data?.customerNameAr;

    const civilId = firstNonEmpty(
      metadata?.civilId,
      metadata?.idNo,
      customer?.nationalNo,
      customer?.nationalId,
      customer?.pId,
      data?.customerNationalNo,
    );
    const agreementNo = firstNonEmpty(
      metadata?.agreementNo,
      data?.title,
      data?.docNo,
    );
    const eventDate = firstNonEmpty(
      metadata?.eventDate,
      metadata?.startDate,
      data?.startDate,
      data?.contractDate,
    );
    const amountText = data?.amount ? moneyFormatEmpty(data?.amount) : '';
    const iban = firstNonEmpty(
      metadata?.iban,
      metadata?.bankIban,
      metadata?.IBAN,
    );
    const reason = firstNonEmpty(metadata?.reason, data?.desc);
    const deliveryDetails = firstNonEmpty(
      metadata?.deliveryDetails,
      data?.desc,
    );
    const customerPhone = firstNonEmpty(
      metadata?.customerPhone,
      customer?.phone,
      customer?.mobile,
      data?.customerPhone,
    );

    const title = isRTL
      ? formTitles?.[formType]?.ar || 'مخالصة عميل'
      : formTitles?.[formType]?.en || 'Customer Clearance';

    const statement = isRTL
      ? this.renderMainArabicStatement({
          formType,
          docDate: data?.time,
          customerName,
          civilId,
          companyName,
          eventDate,
          agreementNo,
          amountText,
          iban,
          reason,
          deliveryDetails,
        })
      : this.renderMainEnglishStatement({
          formType,
          customerName,
          civilId,
          companyName,
          eventDate,
          agreementNo,
          amountText,
          iban,
          reason,
          deliveryDetails,
        });

    return (
      <Box style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <Grid container spacing={0}>
          {this.renderHeader(company)}
          <Grid item xs={12}>
            <Box ml={4} mr={4}>
              {this.renderTitle(title)}
              {this.renderDocInfo(data?.docNo, data?.time, isRTL)}
              <Divider style={{ marginBottom: 15 }} />

              <Box
                border={1}
                borderColor="#ddd"
                p={2}
                style={{ borderRadius: 6, backgroundColor: '#fff' }}
              >
                <Typography
                  style={{
                    fontSize: 12,
                    color: printcolor,
                    whiteSpace: 'pre-line',
                    lineHeight: 2,
                    textAlign: isRTL ? 'right' : 'left',
                  }}
                >
                  {statement}
                </Typography>
              </Box>

              {this.renderSignatureBlock({
                isRTL,
                customerName,
                docDate: data?.time,
                customerPhone,
              })}
            </Box>
          </Grid>
          {this.renderFooter(company)}
        </Grid>
      </Box>
    );
  }
}
